/**
 * Declarative spec for the bot config knobs, grouped into sections by MEANING
 * (not a flat grid). Drives both the defaults editors (org + personal Settings)
 * and the spawn form's advanced popover, so there's one source of truth for
 * what each kind exposes, how it's labelled, and which scope may edit it.
 *
 * Field keys mirror the g10edge strategy config (VanillaConfig /
 * QueueRecyclerConfig). Every field is optional everywhere: an unset value
 * inherits the next layer (personal → org → edge built-in). `orgOnly` sections
 * are strategy decisions a trader shouldn't tune — they appear in the org
 * defaults editor only, never in a trader's personal panel or spawn popover.
 */

export type BotUnit = '¢' | 'ct' | '¢/s' | 'ct/s' | '%';

export interface BotField {
  key: string;
  label: string;
  help?: string;
  unit?: BotUnit;
  /** Edge built-in default, shown as the input placeholder ("inherits N"). */
  builtin?: number;
  /** Reject negatives in the UI (sizes/counts). */
  nonNegative?: boolean;
}

export interface BotSection {
  title: string;
  fields: BotField[];
  /** Strategy-level knobs — org defaults editor only, hidden from traders. */
  orgOnly?: boolean;
}

export interface BotKindSpec {
  /** Edge kind string sent on spawn. */
  kind: string;
  /** Trader-facing family label. */
  label: string;
  /** Fields a trader routinely sets at spawn time (the minimal form shows these
   *  + contracts); everything else lives behind "advanced". */
  primary: string[];
  sections: BotSection[];
}

export const BOT_KIND_SPECS: BotKindSpec[] = [
  {
    kind: 'vanillamm',
    label: 'Vanilla MM',
    primary: ['sizeContracts'],
    sections: [
      {
        title: 'Quote shape',
        fields: [
          { key: 'sizeContracts', label: 'Size', unit: 'ct', help: 'Contracts per side', builtin: 1, nonNegative: true },
          { key: 'belowCents', label: 'Bid offset', unit: '¢', help: 'Below the mid', builtin: 1, nonNegative: true },
          { key: 'aboveCents', label: 'Ask offset', unit: '¢', help: 'Above the mid', builtin: 2, nonNegative: true },
          { key: 'recenterCents', label: 'Recenter', unit: '¢', help: 'Reprice a leg once it drifts beyond this', builtin: 2, nonNegative: true },
        ],
      },
      {
        title: 'Caps',
        fields: [
          { key: 'perSideCapContracts', label: 'Per-side cap', unit: 'ct', help: 'Max resting per side · 0 = uncapped', builtin: 0, nonNegative: true },
        ],
      },
      {
        title: 'Fill avoidance',
        orgOnly: true,
        fields: [
          { key: 'proximityRatioPct', label: 'Proximity', unit: '%', help: 'Recycle when queue ahead falls to this % of placement · 0 = off', builtin: 0, nonNegative: true },
          { key: 'frontDistanceThreshold', label: 'Front distance', unit: 'ct', help: 'Recycle at/under this many contracts ahead · 0 = off', builtin: 0, nonNegative: true },
          { key: 'velocityPullCentsPerSec', label: 'Line-velocity pull', unit: '¢/s', help: 'Pull when the line moves this fast · 0 = off', builtin: 0, nonNegative: true },
          { key: 'depthVelocityPullPerSec', label: 'Depth-velocity pull', unit: 'ct/s', help: 'Pull when queue ahead clears this fast · 0 = off', builtin: 0, nonNegative: true },
          { key: 'polyMoveCents', label: 'Poly-move pull', unit: '¢', help: 'Pull when Polymarket leads against the leg · 0 = off', builtin: 0, nonNegative: true },
          { key: 'closeFrontThreshold', label: 'Close-front', unit: 'ct', help: 'Gate the hard pulls within this many ahead · 0 = auto (25)', builtin: 0, nonNegative: true },
        ],
      },
    ],
  },
  {
    kind: 'queuerecycler',
    label: 'Recycler',
    primary: ['targetContracts'],
    sections: [
      {
        title: 'Quote shape',
        fields: [
          { key: 'targetContracts', label: 'Target', unit: 'ct', help: 'Resting target per side', builtin: 100, nonNegative: true },
          { key: 'chunkContracts', label: 'Chunk', unit: 'ct', help: 'Contracts per recycle chunk', nonNegative: true },
          { key: 'halfSpreadCents', label: 'Half-spread', unit: '¢', help: 'Quote this far off the mid', nonNegative: true },
        ],
      },
      {
        title: 'Band',
        fields: [
          { key: 'bandMinCents', label: 'Band min', unit: '¢', help: 'Lowest price the bot will quote', nonNegative: true },
          { key: 'bandMaxCents', label: 'Band max', unit: '¢', help: 'Highest price the bot will quote', nonNegative: true },
        ],
      },
      {
        title: 'Fill avoidance',
        orgOnly: true,
        fields: [
          { key: 'frontDistanceThreshold', label: 'Front distance', unit: 'ct', help: 'Recycle at/under this many ahead', nonNegative: true },
          { key: 'recenterTolCents', label: 'Recenter tol', unit: '¢', help: 'Reprice tolerance', nonNegative: true },
          { key: 'maxRecyclesPerTick', label: 'Max recycles/tick', unit: 'ct', help: 'Cap recycles per tick', nonNegative: true },
          { key: 'pullToxicityRatioPct', label: 'Pull toxicity', unit: '%', help: 'Pull above this toxicity · 0 = off', nonNegative: true },
          { key: 'velocityPullCentsPerSec', label: 'Velocity pull', unit: '¢/s', help: '0 = off', nonNegative: true },
          { key: 'polyDangerCents', label: 'Poly danger', unit: '¢', help: '0 = off', nonNegative: true },
        ],
      },
    ],
  },
];

export function specForKind(kind: string): BotKindSpec | undefined {
  return BOT_KIND_SPECS.find((s) => s.kind === kind);
}
